/**
 * What a startup still needs before it can be paid (PRD §5.11).
 *
 * The visible missing-field list task 7.9 asks for, and it leads the tab
 * rather than sitting under the form. The failure this exists to prevent is
 * discovering on the day an invoice is due that nobody recorded a VAT id — so
 * the gaps have to be the first thing a reader sees, not something they infer
 * from empty inputs.
 *
 * Two lists, because they mean different things. What is missing from
 * `missingRequired` blocks a purchase order; `missingOptional` is a record
 * that is merely incomplete. Collapsing them would be the "flags everything,
 * so flags nothing" failure the server's short required list avoids.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.9, PRD §5.11, §9.7
 */
import { useTranslation } from 'react-i18next';

import type { LegalFinance } from '@nexus/contracts';

import { Badge } from '../ui/Badge';
import { Gate } from '../ui/Gate';
import { Meter } from '../ui/Meter';

export interface OnboardingReadinessProps {
  record: LegalFinance;
}

export function OnboardingReadiness({
  record,
}: OnboardingReadinessProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="gap-s3 flex flex-col">
      <div className="gap-s2 flex flex-wrap items-center">
        {/* In words, not by colour (PRD §9.7). "Ready" and "not ready" are the
            whole verdict, and a reader who cannot see the tint still needs
            it. */}
        {record.isReadyForOnboarding ? (
          <Badge tone="ok">{t('legalFinance.readiness.ready')}</Badge>
        ) : (
          <Badge tone="warn">
            {t('legalFinance.readiness.blocked', {
              count: record.missingRequired.length,
            })}
          </Badge>
        )}
      </div>

      {/* The meter carries its own readout, so the percentage is NOT also
          stated beside the badge — doing both rendered "33% of the record
          filled in" next to a bare "33", which reads as two different
          figures. Labelled, because a bar with no name tells a
          screen-reader user a percentage and nothing about what it
          measures. */}
      <Meter
        value={record.completeness}
        max={100}
        label={t('legalFinance.readiness.completenessLabel')}
        display={t('legalFinance.readiness.completeness', {
          percent: record.completeness,
        })}
        size="full"
      />

      {record.missingRequired.length > 0 ? (
        <div>
          <p className="text-navy-text mb-s1 text-xs font-semibold">
            {t('legalFinance.readiness.requiredHeading')}
          </p>
          {/* `Gate` with `done={false}`: the same checklist affordance the
              asset page uses for its blockers, so a reader who has seen one
              recognises the other. */}
          {record.missingRequired.map((field) => (
            <Gate key={field} done={false}>
              {t(`legalFinance.fields.${field}`)}
            </Gate>
          ))}
        </div>
      ) : null}

      {record.missingOptional.length > 0 ? (
        <div>
          <p className="text-body mb-s1 text-xs font-semibold">
            {t('legalFinance.readiness.optionalHeading')}
          </p>
          <p className="text-body text-xs font-light">
            {record.missingOptional
              .map((field) => t(`legalFinance.fields.${field}`))
              .join(', ')}
          </p>
        </div>
      ) : null}
    </div>
  );
}
